import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Plus,
  Grid,
  List,
  MoreVertical,
  Edit,
  Trash2,
  Copy,
  Share2,
  BarChart3,
  TrendingUp,
  Database,
} from "lucide-react";
import { dashboardAPI } from "../services/api";
import toast from "react-hot-toast";

const Dashboard: React.FC = () => {
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const queryClient = useQueryClient();

  const {
    data: dashboards = [],
    isLoading,
    error,
  } = useQuery("dashboards", dashboardAPI.getDashboards);

  const deleteDashboardMutation = useMutation(dashboardAPI.deleteDashboard, {
    onSuccess: () => {
      queryClient.invalidateQueries("dashboards");
      toast.success("Dashboard deleted successfully");
    },
    onError: () => {
      toast.error("Failed to delete dashboard");
    },
  });

  const duplicateDashboardMutation = useMutation(
    dashboardAPI.duplicateDashboard,
    {
      onSuccess: () => {
        queryClient.invalidateQueries("dashboards");
        toast.success("Dashboard duplicated successfully");
      },
      onError: () => {
        toast.error("Failed to duplicate dashboard");
      },
    }
  );

  const handleDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this dashboard?")) {
      deleteDashboardMutation.mutate(id);
    }
  };

  const handleDuplicate = (id: string) => {
    duplicateDashboardMutation.mutate(id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">Failed to load dashboards</div>
        <button
          onClick={() => window.location.reload()}
          className="btn-primary"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboards</h1>
          <p className="text-gray-600 mt-1">
            Create and manage your data visualizations
          </p>
        </div>

        <div className="flex items-center space-x-4">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 rounded-md transition-colors ${
                viewMode === "grid"
                  ? "bg-white shadow-sm text-primary-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Grid className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 rounded-md transition-colors ${
                viewMode === "list"
                  ? "bg-white shadow-sm text-primary-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <List className="h-5 w-5" />
            </button>
          </div>

          {/* Create Dashboard Button */}
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>New Dashboard</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 bg-primary-100 rounded-lg">
              <BarChart3 className="h-6 w-6 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Total Dashboards
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {dashboards.length}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Charts</p>
              <p className="text-2xl font-bold text-gray-900">
                {dashboards.reduce(
                  (sum, dashboard) => sum + (dashboard.chart_count || 0),
                  0
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Database className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Data Sources</p>
              <p className="text-2xl font-bold text-gray-900">-</p>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboards List */}
      {dashboards.length === 0 ? (
        <div className="text-center py-12">
          <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No dashboards yet
          </h3>
          <p className="text-gray-600 mb-6">
            Get started by creating your first dashboard
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            Create Dashboard
          </button>
        </div>
      ) : (
        <div className={viewMode === "grid" ? "dashboard-grid" : "space-y-4"}>
          {dashboards.map((dashboard) => (
            <DashboardCard
              key={dashboard.id}
              dashboard={dashboard}
              viewMode={viewMode}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
            />
          ))}
        </div>
      )}

      {/* Create Dashboard Modal */}
      {showCreateModal && (
        <CreateDashboardModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            queryClient.invalidateQueries("dashboards");
          }}
        />
      )}
    </div>
  );
};

interface DashboardCardProps {
  dashboard: any;
  viewMode: "grid" | "list";
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
}

const DashboardCard: React.FC<DashboardCardProps> = ({
  dashboard,
  viewMode,
  onDelete,
  onDuplicate,
}) => {
  const [showMenu, setShowMenu] = useState(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (viewMode === "list") {
    return (
      <div className="card p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-4">
              <Link
                to={`/dashboard/${dashboard.id}`}
                className="text-lg font-semibold text-gray-900 hover:text-primary-600 transition-colors"
              >
                {dashboard.name}
              </Link>
              {dashboard.is_public && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <Share2 className="h-3 w-3 mr-1" />
                  Public
                </span>
              )}
            </div>
            {dashboard.description && (
              <p className="text-gray-600 mt-1">{dashboard.description}</p>
            )}
            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
              <span>{dashboard.chart_count || 0} charts</span>
              <span>•</span>
              <span>Updated {formatDate(dashboard.updated_at)}</span>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Link to={`/dashboard/${dashboard.id}`} className="btn-secondary">
              Open
            </Link>

            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
              >
                <MoreVertical className="h-5 w-5" />
              </button>

              {showMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        onDuplicate(dashboard.id);
                        setShowMenu(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Copy className="h-4 w-4 mr-3" />
                      Duplicate
                    </button>
                    <button
                      onClick={() => {
                        onDelete(dashboard.id);
                        setShowMenu(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-3" />
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <Link
            to={`/dashboard/${dashboard.id}`}
            className="text-lg font-semibold text-gray-900 hover:text-primary-600 transition-colors"
          >
            {dashboard.name}
          </Link>
          {dashboard.description && (
            <p className="text-gray-600 mt-1 text-sm line-clamp-2">
              {dashboard.description}
            </p>
          )}
        </div>

        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
          >
            <MoreVertical className="h-5 w-5" />
          </button>

          {showMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
              <div className="py-1">
                <button
                  onClick={() => {
                    onDuplicate(dashboard.id);
                    setShowMenu(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <Copy className="h-4 w-4 mr-3" />
                  Duplicate
                </button>
                <button
                  onClick={() => {
                    onDelete(dashboard.id);
                    setShowMenu(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-3" />
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>{dashboard.chart_count || 0} charts</span>
        <span>{formatDate(dashboard.updated_at)}</span>
      </div>

      {dashboard.is_public && (
        <div className="mt-3">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <Share2 className="h-3 w-3 mr-1" />
            Public
          </span>
        </div>
      )}
    </div>
  );
};

// Create Dashboard Modal Component
interface CreateDashboardModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const CreateDashboardModal: React.FC<CreateDashboardModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    isPublic: false,
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    try {
      setIsLoading(true);
      await dashboardAPI.createDashboard(formData);
      onSuccess();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to create dashboard");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Create New Dashboard
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">Dashboard Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="input"
                placeholder="Enter dashboard name"
                required
              />
            </div>

            <div>
              <label className="label">Description (Optional)</label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="input"
                rows={3}
                placeholder="Enter dashboard description"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPublic"
                checked={formData.isPublic}
                onChange={(e) =>
                  setFormData({ ...formData, isPublic: e.target.checked })
                }
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label
                htmlFor="isPublic"
                className="ml-2 block text-sm text-gray-900"
              >
                Make this dashboard public
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isLoading || !formData.name.trim()}
              >
                {isLoading ? "Creating..." : "Create Dashboard"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
